import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';

import { ApiService } from '@core/api/api.service';
import {
  ClassificationModelInfo,
  ClassificationProbePrepare,
  ClassificationProbeRun
} from '@core/models/models';

@Component({
  selector: 'app-classify-probe-admin',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './classify-probe-admin.component.html',
  styleUrl: './classify-probe-admin.component.css'
})
export class ClassifyProbeAdminComponent implements OnInit {
  private readonly api = inject(ApiService);

  barcode = '';
  loading = false;
  pickingGold = false;
  runningCategory = false;
  runningType = false;
  errorMessage = '';
  statusMessage = '';

  models: ClassificationModelInfo[] = [];
  selectedModel = '';
  includePhoto = true;

  prepared: ClassificationProbePrepare | null = null;
  categoryLocked = false;
  categorySlug: string | null = null;
  goldCategory: string | null = null;
  goldType: string | null = null;

  categoryDecision = '';
  categoryWhy = '';
  categorySystem = '';
  categoryUser = '';
  typeSystem = '';
  typeUser = '';
  typeReady = false;

  categoryResult: ClassificationProbeRun | null = null;
  typeResult: ClassificationProbeRun | null = null;

  ngOnInit(): void {
    void this.loadModels();
  }

  get availableModels(): ClassificationModelInfo[] {
    return this.models.filter((m) => m.available);
  }

  get modelOptions(): ClassificationModelInfo[] {
    if (this.categoryDecision === 'category_api' && !this.categoryLocked) {
      return this.availableModels.filter(
        (m) => m.id === 'gemini-2.5-pro' || m.id === 'gpt-5-mini'
      );
    }
    return this.availableModels;
  }

  get busy(): boolean {
    return this.loading || this.pickingGold || this.runningCategory || this.runningType;
  }

  async loadModels(): Promise<void> {
    try {
      const res = await firstValueFrom(this.api.listClassificationModels());
      this.models = res.models || [];
      const preferred =
        this.models.find((m) => m.id === 'gemini-2.5-pro' && m.available) ||
        this.models.find((m) => m.id === 'gemini-2.5-flash' && m.available) ||
        this.models.find((m) => m.default && m.available) ||
        this.models.find((m) => m.available);
      this.selectedModel = preferred?.id || res.default || '';
    } catch {
      this.models = [];
    }
  }

  async loadRandomGold(): Promise<void> {
    this.pickingGold = true;
    this.errorMessage = '';
    this.statusMessage = '';
    try {
      const sample = await firstValueFrom(this.api.randomGoldClassificationProbe());
      const code = (sample.barcode || '').trim();
      if (!code) {
        this.errorMessage = 'No barcode found in the gold set.';
        return;
      }
      this.barcode = code;
      this.goldCategory = sample.goldCategory || null;
      this.goldType = sample.goldType || null;
      this.statusMessage = `Picked gold ${sample.goldCategory}/${sample.goldType} — loading prompts…`;
      await this.load(true);
    } catch (err) {
      this.errorMessage = this.readError(err, 'Failed to pick a gold barcode.');
    } finally {
      this.pickingGold = false;
    }
  }

  async load(keepGold = false): Promise<void> {
    const code = this.barcode.trim();
    if (!code) {
      this.errorMessage = 'Enter a barcode.';
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    if (!keepGold) {
      this.statusMessage = '';
      this.goldCategory = null;
      this.goldType = null;
    }
    this.resetFlow();
    try {
      const prepared = await this.prepare(code, 'group');
      this.prepared = prepared;
      this.barcode = prepared.barcode || code;
      this.categoryDecision = prepared.categoryDecision || 'category_api';
      this.categoryWhy = prepared.categoryWhy || '';
      const goldHint =
        this.goldCategory && this.goldType
          ? ` Gold: ${this.goldCategory}/${this.goldType}.`
          : '';
      this.categorySystem = prepared.system;
      this.categoryUser = prepared.user;
      if (prepared.categoryModel) {
        this.selectedModel = prepared.categoryModel;
      }
      this.statusMessage = prepared.hasImageAvailable
        ? `Category prompts ready. Edit, then Generate category.${goldHint}`
        : `Category prompts ready. No product image on file.${goldHint}`;
    } catch (err) {
      this.prepared = null;
      this.errorMessage = this.readError(err, 'Failed to load prompts.');
    } finally {
      this.loading = false;
    }
  }

  async onIncludePhotoChange(value: boolean): Promise<void> {
    this.includePhoto = value;
    if (!this.prepared || this.busy) {
      return;
    }
    const code = this.barcode.trim();
    if (!code) {
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    try {
      if (!this.categoryLocked) {
        const prepared = await this.prepare(code, 'group');
        this.prepared = prepared;
        this.categorySystem = prepared.system;
        this.categoryUser = prepared.user;
        this.categoryWhy = prepared.categoryWhy || this.categoryWhy;
        this.statusMessage = 'Category prompts refreshed (photo).';
      } else if (this.categorySlug) {
        const prepared = await this.prepare(code, 'sub_group', this.categorySlug);
        this.typeSystem = prepared.system;
        this.typeUser = prepared.user;
        this.typeReady = true;
        this.statusMessage = `Type prompts refreshed under ${this.categorySlug}.`;
      }
    } catch (err) {
      this.errorMessage = this.readError(err, 'Failed to refresh prompts.');
    } finally {
      this.loading = false;
    }
  }

  async generateCategory(): Promise<void> {
    if (this.categoryLocked) {
      return;
    }
    const code = this.barcode.trim();
    if (!this.canGenerate(code, this.categorySystem, this.categoryUser)) {
      return;
    }

    this.runningCategory = true;
    this.errorMessage = '';
    this.statusMessage = '';
    this.typeResult = null;
    this.typeReady = false;
    this.typeSystem = '';
    this.typeUser = '';
    try {
      const run = await this.runProbe(code, this.categorySystem, this.categoryUser);
      this.categoryResult = run;
      const slug = this.readExistingSlug(run);
      if (!slug) {
        this.statusMessage =
          'Category did not return an existing slug — no Type step (none/abstain).';
        return;
      }

      this.categorySlug = slug;
      this.categoryLocked = true;
      this.statusMessage = `Category: ${slug}. Loading type prompts…`;
      await this.loadTypeSection(code, slug);
    } catch (err) {
      this.errorMessage = this.readError(err, 'Category run failed.');
    } finally {
      this.runningCategory = false;
    }
  }

  async generateType(): Promise<void> {
    if (!this.typeReady || !this.categorySlug) {
      this.errorMessage = 'No type prompts — run category first.';
      return;
    }
    const code = this.barcode.trim();
    if (!this.canGenerate(code, this.typeSystem, this.typeUser)) {
      return;
    }

    this.runningType = true;
    this.errorMessage = '';
    this.statusMessage = '';
    try {
      const run = await this.runProbe(code, this.typeSystem, this.typeUser);
      this.typeResult = run;
      this.statusMessage = `Type done under ${this.categorySlug} (no DB write).`;
    } catch (err) {
      this.errorMessage = this.readError(err, 'Type run failed.');
    } finally {
      this.runningType = false;
    }
  }

  parsedJson(run: ClassificationProbeRun | null): string {
    if (!run?.parsed) {
      return '';
    }
    return JSON.stringify(run.parsed, null, 2);
  }

  private resetFlow(): void {
    this.categoryLocked = false;
    this.categorySlug = null;
    this.categoryDecision = '';
    this.categoryWhy = '';
    this.categorySystem = '';
    this.categoryUser = '';
    this.typeSystem = '';
    this.typeUser = '';
    this.typeReady = false;
    this.categoryResult = null;
    this.typeResult = null;
  }


  private async loadTypeSection(code: string, categorySlug: string): Promise<void> {
    this.loading = true;
    try {
      const prepared = await this.prepare(code, 'sub_group', categorySlug);
      this.typeSystem = prepared.system;
      this.typeUser = prepared.user;
      this.typeReady = true;
      this.statusMessage = `Type prompts ready under ${categorySlug}. Edit, then Generate type.`;
    } catch (err) {
      this.typeReady = false;
      this.errorMessage = this.readError(err, 'Failed to load type prompts.');
    } finally {
      this.loading = false;
    }
  }

  private async prepare(
    code: string,
    step: 'group' | 'sub_group',
    categorySlug: string | null = null
  ): Promise<ClassificationProbePrepare> {
    return firstValueFrom(
      this.api.prepareClassificationProbe({
        barcode: code,
        includePhoto: this.includePhoto,
        step,
        categorySlug: step === 'sub_group' ? categorySlug : null
      })
    );
  }

  private async runProbe(
    code: string,
    system: string,
    user: string
  ): Promise<ClassificationProbeRun> {
    return firstValueFrom(
      this.api.runClassificationProbe({
        barcode: code,
        system,
        user,
        model: this.selectedModel,
        includePhoto: this.includePhoto
      })
    );
  }

  private canGenerate(code: string, system: string, user: string): boolean {
    if (!code) {
      this.errorMessage = 'Enter a barcode.';
      return false;
    }
    if (!system.trim() || !user.trim()) {
      this.errorMessage = 'System and user prompts are required.';
      return false;
    }
    if (!this.selectedModel) {
      this.errorMessage = 'Select a model.';
      return false;
    }
    return true;
  }

  private readExistingSlug(run: ClassificationProbeRun): string | null {
    const parsed = run.parsed || {};
    const decision = typeof parsed['decision'] === 'string' ? parsed['decision'] : '';
    const slug = typeof parsed['slug'] === 'string' ? parsed['slug'].trim() : '';
    if (decision !== 'existing' || !slug) {
      return null;
    }
    return slug;
  }

  private readError(err: unknown, fallback: string): string {
    if (err instanceof HttpErrorResponse) {
      const body = err.error;
      if (body && typeof body === 'object' && 'error' in body && typeof body.error === 'string') {
        return body.error;
      }
      if (typeof body === 'string' && body.trim()) {
        return body;
      }
      return `${fallback} (HTTP ${err.status})`;
    }
    return fallback;
  }
}
